/**
 * Client-side validation. Mirrors the backend's payload-validator so the
 * renderer can surface inline errors before submit.
 */

import type { FormBlock } from '@school-journey/shared-types/schemas'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^[+0-9()\-.\s]{5,}$/

export interface FieldError {
  fieldId: string
  message: string
}

export function validateBlock(block: FormBlock, value: unknown): FieldError | null {
  // Decoration blocks have nothing to validate.
  if (block.type === 'PageBreak' || block.type === 'SectionHeader' || block.type === 'Markdown') {
    return null
  }

  const id = block.props.id
  const required = 'required' in block.props ? Boolean(block.props.required) : false
  const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

  if (required && isEmpty) {
    return { fieldId: id, message: `${block.props.label ?? id} is required` }
  }

  if (isEmpty) return null

  switch (block.type) {
    case 'Email':
      if (typeof value !== 'string' || !EMAIL_RE.test(value)) {
        return { fieldId: id, message: 'Enter a valid email address' }
      }
      return null
    case 'Phone':
      if (typeof value !== 'string' || !PHONE_RE.test(value)) {
        return { fieldId: id, message: 'Enter a valid phone number' }
      }
      return null
    case 'Date':
      if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
        return { fieldId: id, message: 'Enter a valid date' }
      }
      return null
    case 'Select':
      if (typeof value !== 'string' || !block.props.options.includes(value)) {
        return { fieldId: id, message: 'Choose a valid option' }
      }
      return null
    case 'MultiSelect':
      if (!Array.isArray(value) || value.some(v => typeof v !== 'string' || !block.props.options.includes(v))) {
        return { fieldId: id, message: 'Choose valid options' }
      }
      return null
    default:
      return null
  }
}
